/*
 * Utils.ts
 *
 * Module for utility functions
 */

import { Utils } from "@ms-ofb/officefloodgatecore";
import * as Constants from "./Constants";
const { guid, overrideValues, isNOU, isBoolean, isString, isObject, isEnumValue } = Utils;
export { guid, isBoolean, isNOU, isObject, isString, overrideValues };

/**
 * Check if given value is a number
 * @param {any} value value
 */
function isNumber(value: any): boolean {
	return (typeof value === "number");
}

/**
 * Check if given value is an integer
 * @param {any} value value
 */
export function isInteger(value: any): boolean {
	return typeof value === "number" &&
		isFinite(value) &&
		Math.floor(value) === value;
}

/**
 * Check if value is a valid guid
 * @param {any} value value
 */
function isGuid(value: any): boolean {
	return (isString(value) &&
		/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value));
}

/**
 * Throw if not object
 */
export function expectObject(value: any, name: string): void {
	if (!isObject(value)) {
		throw name + " is not an object: " + value;
	}
}

/**
 * Throw if not number
 */
export function expectNumber(value: any, name: string): void {
	if (!isNumber(value)) {
		throw name + " is not a number: " + value;
	}
}

/**
 * Throw if not string
 */
export function expectString(value: any, name: string): void {
	if (!isString(value)) {
		throw name + " is not a string: " + value;
	}
}

/**
 * Throw if not boolean
 */
export function expectBoolean(value: any, name: string): void {
	if (!isBoolean(value)) {
		throw name + " is not a boolean: " + value;
	}
}

/**
 * Throw if not guid
 */
export function expectGuid(value: any, name: string): void {
	if (!isGuid(value)) {
		throw name + " is not a guid: " + value;
	}
}

/**
 * Throw if not array
 */
export function expectArray(value: any, name: string): void {
	if (!Array.isArray(value)) {
		throw name + " is not an array: " + value;
	}
}

/**
 * Throw if not valid enum
 * Note: this won't work with const enum
 */
export function expectEnum(value: any, enumType: any, name: string): void {
	if (!isNumber(value) || !isEnumValue(value, enumType)) {
		throw name + " is not an enum: " + value;
	}
}

/**
 * Throw if not number or string or boolean
 */
export function expectSimpleType(value: any, name: string): void {
	if (!isString(value) && !isNumber(value) && !isBoolean(value)) {
		throw name + " is not a string or number or boolean: " + value;
	}
}

/**
 * throw if not a function
 */
export function expectFunction(value: any, name: string): void {
	if (isNOU(value) && typeof value !== "function") {
		throw name + " is not a function: " + value;
	}
}

// Creates a promise that rejects in <ms> milliseconds and
// returns the race between the created timeout promise and the passed in promise
export function createTimeoutPromise<T>(ms: number, promise: Promise<T>): Promise<T> {
	// Create a promise that rejects in <ms> milliseconds
	const timeout = new Promise((resolve, reject) => {
		const id = setTimeout(() => {
			clearTimeout(id);
			reject("Timed out in " + ms + "ms.");
		}, ms);
	});

	// Returns a race between our timeout and the passed in promise
	return Promise.race([promise, timeout]) as Promise<T>;
}

export function isTenantIdCommercial (tenantId: string): boolean {

	// if tenantId is not defined correctly -> consumer
	if (isNOU(tenantId) || tenantId === "" || !isGuid(tenantId)) {
		return false;
	}

	const lowerTenantId = tenantId.toLowerCase();

	// if id is all zeros or one of the special consumer tenant ids -> consumer
	// For further reference see how OCV treats these IDs: 
	// https://office.visualstudio.com/CLE/_git/OfficeCustomerVoice?path=%2FIngestion%2FIngestion.Floodgate%2FFloodgateDataExtractor.cs
	if (lowerTenantId === "00000000-0000-0000-0000-000000000000"
		|| lowerTenantId === "9188040d-6c67-4c5b-b112-36a304b66dad"
		|| lowerTenantId === "84df9e7f-e9f6-40af-b435-aaaaaaaaaaaa") {

		return false;
	}

	// any other case -> commercial
	return true;
}

/**
 * Convert the policy value from Office Control Policy Service (ocps) to string
 * @param policyValue 
 * @returns string
 */
export function ocpsBooleanToString(policyValue?: boolean) {
	if (policyValue === undefined) {
		return "Not Configured";
	}

	if (policyValue) {
		return "Enabled";
	}

	return "Disabled";
}

/**
 * Convert the policy enum from Office Control Policy Service (ocps) to string
 * @param policyValue 
 * @returns string
 */
export function ocpsEnumToString(policyValue?: Constants.OCPSValues) {
	if (!policyValue) {
		return "Not Configured";
	}
	switch (policyValue) {
		case Constants.OCPSValues.Disabled:
			return "Disabled";
		case Constants.OCPSValues.Enabled:
			return "Enabled";
		default:
			return "Not Configured";
	}
}

/**
 * Convert AutoDismiss enum to time in miliseconds
 * @return {number} time in miliseconds
 */
export function convertAutoDismissToTimeInMs(autoDismiss: Constants.AutoDismissValues): number {
	let autoDismissDuration: number;

	switch (autoDismiss) {
		case (Constants.AutoDismissValues.SevenSeconds):
			autoDismissDuration = 7000;
			break;
		case (Constants.AutoDismissValues.FourteenSeconds):
			autoDismissDuration = 14000;
			break;
		case (Constants.AutoDismissValues.TwentyOneSeconds):
			autoDismissDuration = 21000;
			break;
		case (Constants.AutoDismissValues.TwentyEightSeconds):
			autoDismissDuration = 28000;
			break;
		case (Constants.AutoDismissValues.SixtySeconds):
			autoDismissDuration = 60000;
			break;
		case (Constants.AutoDismissValues.NinetySeconds):
			autoDismissDuration = 90000;
			break;
		case (Constants.AutoDismissValues.OneHundredAndTwentySeconds):
			autoDismissDuration = 120000;
			break;
		case (Constants.AutoDismissValues.OneHundredAndFiftySeconds):
			autoDismissDuration = 150000;
			break;
		default:
			autoDismissDuration = 0;
			break;
	}
	return autoDismissDuration;
}

/**
 * Scrubs XML, HTML, and other privacy fields from string content
 * @return scrubbed string
 */
export function scrubContent(input: string){
	if(!input){
		return "";
	}
	const tagPatterns = [
		/<\?XML[\s\S]*?<\/\?XML>/gi, //removing XML tags and content within
		/<\?XML[\s\S]*?\?>/gi,
		/<[^>]*>[\s\S]*?<\/[^>]*>/gi, // removing HTML content
        /<[^>]*>/g,
        /]*>/gi,
		/<ASP:[\s\S]*?<\/ASP:[^>]*>/gi, //removing ASP content
		/MIME-VERSION:.*?([,\n]|$)/gi,
		/{\\RTF[\s\S]*?}/gi,
		/ACCESS_TOKEN:.*?([.,\n]|$)/gi,
		/AWSACCESSKEYID:.*?([.,\n]|$)/gi,
		/([A-Z0-9_]|\.|%2E){1,50}(@|_AT_|%40)([A-Z0-9_]{1,50}(\.|_|%2E))+(COM|ORG|GOV|EDU)/gi, // removing email addresses
		/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/gi // removing ip addresses
	];

	let scrubbedContent = input;
	tagPatterns.forEach(pattern => {
		scrubbedContent = scrubbedContent.replace(pattern, 'redacted');
	});
	return scrubbedContent.replace(/\s+/g, ' ').trim();
}